'use client';

import React, { useCallback } from 'react';
import { Button } from 'reactstrap';

import { formatCurrency } from 'src/helpers/utils';
import type { ActivityLogEntry } from 'src/types';

interface QuotesActivityLogCardProps {
  logDetails: ActivityLogEntry;
}

const QuotesActivityLogCard: React.FC<QuotesActivityLogCardProps> = ({ logDetails }) => {
  const eventAttrs = logDetails?.event_attributes;
  const serviceCategory = logDetails?.service_category

  const getLogMessage = useCallback((): React.ReactNode => {
    const amountFormatted = formatCurrency(Number(eventAttrs?.amount) || 0, 2, 2);
    const categoryLabel = serviceCategory?.label;
    const jobNo = logDetails?.job_no;
    const approvedBy = eventAttrs?.approved_by;
    const type = eventAttrs?.type;
    const isOnsiteQuote = eventAttrs?.is_onsite_quote || false;
    const isOnsiteQuoteExpired = eventAttrs?.onsite_quote_expired || false;
    switch (eventAttrs?.to?.value) {
      case 'submitted-for-customer-approval':
      case 'make-ready-quote-submitted':
        case 'quote-submitted':
        if (isOnsiteQuoteExpired) {
          return 'The on-site approval time has expired, and the pro has left the property.';
        }
        if (isOnsiteQuote) {
          return (
            <React.Fragment>
              New on-site quote of
              {' '}
              <span>{amountFormatted || '-'}</span>
              {' '}
              has been received for the
              {' '}
              {categoryLabel || '-'}
              {' '}
              Job
            </React.Fragment>
          );
        }
        return `New quote with amount of ${amountFormatted || '-'} received for ${categoryLabel || '-'} (#${jobNo || '-'}).`;
      case 'quote-accepted':
      case 'make-ready-quote-approved':
        return `Quote for ${categoryLabel || '-'} (#${jobNo || '-'}) totaling to ${amountFormatted || '-'}, ${type === 'partially'
            ? 'partially approved'
            : 'approved'} by ${approvedBy}.`;
      case 'expired':
        return `Quote for ${categoryLabel || '-'} (#${jobNo || '-'}) totaling to ${amountFormatted || '-'} has expired`;
      case 'quote-rejected':
      case 'make-ready-quote-rejected':
        return `Quote for ${categoryLabel || '-'} (#${jobNo || '-'}) totaling to ${amountFormatted || '-'} has been rejected`;
      default:
        return '';
    }
  }, [eventAttrs?.amount,
    eventAttrs?.approved_by,
    eventAttrs?.type,
    eventAttrs?.is_onsite_quote,
    eventAttrs?.onsite_quote_expired,
    eventAttrs?.to?.value,
    serviceCategory?.label,
    logDetails?.job_no]);

  const openQuoteDataInNewTab = useCallback((data: ActivityLogEntry ) => {
    const toVal = eventAttrs?.to?.value;
    if (
      toVal === 'submitted-for-customer-approval'
      && eventAttrs?.quote_url
    ) {
      window.open(eventAttrs?.quote_url, '_blank');
    } else {
      window.open(`/quotes?quote_no=${data?.quote_no}`, '_blank');
    }
  }, [eventAttrs?.quote_url, eventAttrs?.to?.value]);

  const quoteLabels = useCallback((data: ActivityLogEntry): string => {
    const dataEventAttrs = data?.event_attributes;
    switch (dataEventAttrs?.to?.value) {
      case 'submitted-for-customer-approval':
      case 'make-ready-quote-submitted':
        case 'quote-submitted':
        // if it's not an onsite quote and not expired, show the label
        if (!dataEventAttrs?.is_onsite_quote
          && !dataEventAttrs?.onsite_quote_expired) {
          return 'New Quote Received';
        }
        return '';
      case 'quote-accepted':
      case 'make-ready-quote-approved':
        if (dataEventAttrs?.type === 'partially') {
          return 'Quote Approved Partially';
        }
        return 'Quote Approved';
      case 'quote-rejected':
      case 'make-ready-quote-rejected':
        return 'Quote Rejected';
      case 'expired':
        return 'Quote Expired';
      default:
        return '';
    }
  }, []);

  const isOnsiteQuoteTripFeeEnabled = !!(
    eventAttrs?.is_onsite_quote && eventAttrs?.is_onsite_quote_trip_fee_enabled
  );

  const toValue = eventAttrs?.to?.value;

  return (
    <React.Fragment>
      <p className="mb-0"><span>{quoteLabels(logDetails)}</span></p>
      <p className="mb-0">
        {getLogMessage()}
        {' '}
        <Button
          type="button"
          color="link"
          onClick={() => openQuoteDataInNewTab(logDetails)}
        >
          {(toValue
            === 'submitted-for-customer-approval'
            && eventAttrs?.quote_url)
            ? 'Review Quote' : ''}
          {[
            'quote-accepted',
            'expired',
            'quote-rejected',
          ]?.includes(toValue || '')
            ? 'View Details' : ''}
        </Button>
      </p>
      {isOnsiteQuoteTripFeeEnabled && (
        <p>
          Approve the quote while the technician is on-site to receive a
          {' '}
          {formatCurrency((eventAttrs?.onsite_quote_trip_fee) || 0, 2, 2)}
          {' '}
          discount for avoiding a second trip.
        </p>
      )}
    </React.Fragment>
  );
};
export default QuotesActivityLogCard;
